/* eslint-disable no-console */
import axios from 'axios'
import { camelizeKeys } from 'humps'

import Config from '@/config'
import compose from '@/utils/compose'
import Cookies from '@/utils/cookie-provider'

import store from '@/store'
import router from '@/plugins/router'
import logger from '@/utils/logger'
import clientLogger from '@/plugins/logger/core'

import { toString } from '@/utils/string'
import { isNetworkError } from '@/utils/network'
import { HttpRequestError } from '@/utils/error'
import attachment from '@/utils/request-attachment'
import { waitFor, loopUtil, expBackoffDelay } from '@/utils/timer'
import { MFA_SESSION_ENDPOINTS } from '@/constants/mfa-api-endpoints'

import PAYMENT_TYPES from '@/constants/payment-types'
import LOGGING_EVENTS from '@/constants/logging-events'

const validateStatus = (status) => status >= 200 && status < 400

const fetcher = async ({ path, trackingKey, baseUrl, ...params }) => {
  const logMethod = params?.method ?? ''
  const logBaseUrl = baseUrl ?? Config.apiBaseUrl

  logger.info(`Request ${logMethod} ${logBaseUrl}${path}`)

  try {
    const response = await axios({
      url: path,
      baseURL: baseUrl ?? Config.apiBaseUrl,
      timeout: 60000,
      // set the  validator function here, to avoid unnecessary custom throwing logic
      validateStatus,
      ...params,
    })

    logger.info(
      `Response ${response?.status ?? ''} ${logMethod} ${logBaseUrl}${path}`
    )

    return response
  } catch (error) {
    const errorCode = HttpRequestError.getErrorCode(error)
    const errorObj = HttpRequestError.parse(error)
    const { status, data, message } = errorObj
    const { method, url: apiUrl } = error?.config || {}
    const errorPayload = {
      statusCode: status,
      code: errorCode,
      message: data?.message || message,
      title: data?.title,
      method,
      apiUrl,
    }
    error.trackingKey = trackingKey

    clientLogger.error(errorCode || LOGGING_EVENTS.REQUEST_CALL_FAILURE, {
      errorPayload,
    })

    throw error
  }
}

const validMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']
const isValidMethod = (method) => validMethods.includes(method)

const onlyTrue =
  (options = {}) =>
  ([handlerName]) =>
    options[handlerName]

const makeHandlers = (handlers = {}, options = {}) =>
  Object.entries(handlers)
    .filter(onlyTrue(options))
    .map(([, func]) => func)

const makeFetchParams = ({
  method = 'GET',
  trackingKey,
  headers,
  data,
  baseUrl,
}) => {
  if (!isValidMethod(method)) {
    throw new TypeError(
      `Method [${method}] is invalid. The method must be one of [${validMethods}]`
    )
  }

  const dataKey = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)
    ? 'data'
    : 'params'

  return {
    headers,
    method,
    trackingKey,
    baseUrl,
    [dataKey]: data,
  }
}

const builderHeaders = ({ options = {} }) => {
  const customHeaders = options.headers || {}
  const handlers = makeHandlers(attachment.headers, options)
  const mainHandler = compose(...handlers)

  const { headers } = mainHandler({ headers: {} })

  return {
    'x-country': store.getters['session/countryCode'],
    ...headers,
    ...customHeaders,
    'x-component': 'checkout',
    'x-locale': store.getters['application/currentLocale'],
    'x-ui-version': Config.releaseVersion,
    ...(() => {
      try {
        const mobileAppVersion = Cookies.get('tamara-app-version')

        return mobileAppVersion && mobileAppVersion !== ''
          ? {
              'x-mobile-app-version': mobileAppVersion,
            }
          : {}
      } catch {
        return {}
      }
    })(),
  }
}

const parseResponse = (res, options = {}) => {
  const status = res && res.status

  const { data, headers } = res

  if (options.camelizeRes) {
    return camelizeKeys({ data, headers, status }, (key, convert) => {
      const IGNORE_KEYS = Object.values(PAYMENT_TYPES)

      return IGNORE_KEYS.includes(key) ? key : convert(key)
    })
  }

  return { data, headers, status }
}

const makeHandleNetworkError = (doFetch) => async () => {
  try {
    store.dispatch('network/retryNow')
    const { isTimedOut, ...result } = await loopUtil(
      async () => {
        try {
          logger.trace('Retrying request....')
          const response = await doFetch()
          logger.trace('Retry request succeeded')
          return response
        } catch (error) {
          logger.trace('Retry request got error', { error })
          if (isNetworkError(error)) {
            return { networkError: error }
          }
        }
      },
      (res) => {
        const isNetworkError = res?.networkError

        if (responseLockedHandlerEnabled()) {
          return !isNetworkError && !isResponseLocked(res)
        }

        return !isNetworkError
      },
      expBackoffDelay(3_000, 30_000),
      (meta) => {
        store.dispatch('network/updateTimeoutMs', meta.timeoutMs)
      }
    )

    if (isTimedOut) {
      store.dispatch('network/retryError')
    } else {
      store.dispatch('network/retrySuccess')
    }

    return result
  } catch (error) {
    logger.error('Retry request error', { error })
    store.dispatch('network/retryError')
  }
}

const handleUnauthorisedError = () => {
  const { query: currentQuery = {} } = router || {}
  // reset customer data and redirect to the login page
  store.dispatch('auth/resetAuth')
  router.replace({
    name: 'login',
    query: { ...currentQuery, redirectFrom: router.path },
  })
}

const redirectToLogin = () => {
  const queryParams = Object.fromEntries(
    new URLSearchParams(window.location.search)
  )
  router.replace({ path: '/login', query: queryParams })
}

const handleVpnDetected = (response) => {
  // if vpn is detected, redirect to vpn error page
  const queryParams = Object.fromEntries(
    new URLSearchParams(window.location.search)
  )
  const isSingleCheckout = store.getters['session/singleCheckoutEnabled']
  store.dispatch('auth/resetAuth')
  router.replace({
    path: '/vpn-error',
    query: {
      ...queryParams,
      single_checkout: isSingleCheckout ? 'true' : 'false',
    },
  })
}

// For unit test
export { fetcher, makeFetchParams, parseResponse, builderHeaders }

async function sender(payload = {}, meta = {}) {
  const { path, method, data, baseUrl, trackingKey } = payload
  const { options = {} } = meta

  const doFetch = (() => {
    let count = 0
    const fakeNetworkErrorCount = options.fakeNetworkErrorCount || 0

    return async () => {
      if (Config.isDev && count < fakeNetworkErrorCount) {
        count++
        await waitFor(1500)
        throw Error('Fake network error')
      }

      // Auto attach token from state
      const headers = builderHeaders({
        options: { attachToken: true, attachDeviceId: true, ...options },
      })
      const params = makeFetchParams({
        method,
        trackingKey,
        headers,
        data,
        baseUrl,
      })

      const response = await fetcher({ path, ...params })

      // Redirect to login page if customer is on vpn detected page
      const currentPath = window.location.pathname
      if (currentPath === '/vpn-error') {
        redirectToLogin()
      }

      return parseResponse(response, { camelizeRes: true, ...options })
    }
  })()

  const handleNetworkError = makeHandleNetworkError(doFetch)

  try {
    return await doFetch()
  } catch (error) {
    const response = error.response || {}
    const checkoutId = store.getters['session/checkoutId']
    const orderId = store.getters.orderId || ''

    logger.debug(
      error.message,
      { data: { path, method, response } },
      {
        category: 'request',
        'order.checkoutId': checkoutId,
        'order.id': orderId,
      }
    )

    const isAuthenticationRequest = isAuthEndpoints(path)

    // Network error, we have response, but no status code
    // if the path is an auth endpoint, we don't want to retry the request
    // Skip network error retry for authentication requests
    if (!isAuthenticationRequest) {
      if (
        isNetworkError(error) ||
        (responseLockedHandlerEnabled() &&
          isResponseLocked(error?.response ?? {}))
      ) {
        return handleNetworkError(error)
      }
    }

    // vpn detected
    if (response.status === 420) {
      // log the error
      logger.error(error, { path, method, response }, { category: 'request' })

      console.log('VPN detected in response')

      return handleVpnDetected()
    }

    if (response.status >= 500) {
      logger.error(error, { path, method, response }, { category: 'request' })

      if (!shouldSkipMaintenanceRedirectOnServerError(path, options)) {
        window.location.href = 'https://tamara.co/maintenance.html'

        return
      }
    }

    if (
      toString(response.status) === '401' ||
      toString(response.status) === '403'
    ) {
      handleUnauthorisedError()
    }

    throw new HttpRequestError(error.message, response, trackingKey)
  }
}

const isResponseLocked = (response) => response?.status === 423

export const responseLockedHandlerEnabled = () => {
  try {
    if (!window?.statsigClient?.checkGate) return true

    return (
      window?.statsigClient?.checkGate('response_locked_handler_enabled') ??
      true
    )
  } catch (err) {
    console.error(err)

    return true
  }
}

const isAuthEndpoints = (path) => {
  return path.includes('/customers/authenticate')
}

/** Path fragments: if `path` includes any segment, 5xx responses skip the maintenance redirect. */
const MAINTENANCE_REDIRECT_EXEMPT_PATH_SEGMENTS = [
  MFA_SESSION_ENDPOINTS.stepAction,
  MFA_SESSION_ENDPOINTS.stepTransition,
]

export const isMaintenanceRedirectExempt = (
  path,
  segments = MAINTENANCE_REDIRECT_EXEMPT_PATH_SEGMENTS
) => segments.some((segment) => path.includes(segment))

const shouldSkipMaintenanceRedirectOnServerError = (path, options) =>
  options.skipMaintenanceRedirectOnServerError === true ||
  isMaintenanceRedirectExempt(path)

// Usage - See more at @docs/request-util.md
export default sender
